import type { ListingSide } from '../b2bSideCopy';
import type {
  ContractDetail,
  ContractListItem,
  ContractRelatedEmployer,
  ContractRelatedProvider,
} from './types';

// How a party is named on a contract. An MSAAS contract names its parties Buyer and Supplier, since
// either side can post the listing; JOB and SERVICE contracts keep Hiring and Hired.
export type ContractRole = 'Hiring' | 'Hired' | 'Buyer' | 'Supplier';

export interface ContractParty {
  name: string;
  label: string;
  role: ContractRole;
}

// `listing` is the side that posted the listing and `contracted` the side that answered it, whatever
// they are called: the signature sides and the backend role filter are split the same way.
export interface ContractParties {
  listing: ContractParty;
  contracted: ContractParty;
}

const MISSING_PARTY = 'TBD';

const employerName = (employer: ContractRelatedEmployer | null | undefined) =>
  employer?.employerName ?? MISSING_PARTY;

const providerName = (provider: ContractRelatedProvider | null | undefined) =>
  provider ? `${provider.firstName} ${provider.lastName}` : MISSING_PARTY;

export const getContractListingTitle = (contract: ContractDetail): string | null =>
  contract.jobListing?.jobTitle ??
  contract.msaasListing?.listingTitle ??
  contract.providerServiceListing?.listingTitle ??
  null;

// [MCV-1889] Which side an MSAAS contract's listing is on. The detail carries it on the listing; the
// flat list payload may not, and a missing value is a SEEKING listing, as every listing was.
export const getContractListingSide = (
  contract: ContractListItem & Partial<Pick<ContractDetail, 'msaasListing'>>
): ListingSide => contract.msaasListing?.listingSide ?? contract.listingSide ?? 'SEEKING';

// The roles of the listing side and the contracted side. The buyer is the listing employer of a
// SEEKING listing and the contracted employer of an OFFERING one (DD27).
export const getContractRoles = (
  contract: ContractListItem & Partial<Pick<ContractDetail, 'msaasListing'>>
): { listing: ContractRole; contracted: ContractRole } => {
  if (contract.listingType !== 'MSAAS') return { listing: 'Hiring', contracted: 'Hired' };
  return getContractListingSide(contract) === 'OFFERING'
    ? { listing: 'Supplier', contracted: 'Buyer' }
    : { listing: 'Buyer', contracted: 'Supplier' };
};

// [MCV-1725] The listing slot is the listing side of the contract and the contracted slot is the
// contracted side. The backend's list role filter splits the same way for JOB, SERVICE and SEEKING
// MSAAS contracts (HIRING = listing_employer_id, HIRED = contracted_employer_id) and flips it for
// OFFERING ones, where the buyer is the contracted employer; it also decides which signature a party
// records. JOB and MSAAS contracts list from an employer; a SERVICE contract lists from a provider's
// service listing, so it has no listing employer: the provider takes the listing slot and the
// inquiring employer the contracted slot. That was a deliberate product call for MCV-1725; flipping
// it would mean changing the backend role filter as well.
export const getContractParties = (contract: ContractDetail): ContractParties => {
  const roles = getContractRoles(contract);
  switch (contract.listingType) {
    case 'SERVICE':
      return {
        listing: {
          name: providerName(contract.listingProvider),
          label: '(Service Listing)',
          role: roles.listing,
        },
        contracted: {
          name: employerName(contract.contractedEmployer),
          label: '(Inquiry)',
          role: roles.contracted,
        },
      };
    case 'MSAAS': {
      const offering = getContractListingSide(contract) === 'OFFERING';
      return {
        listing: {
          name: employerName(contract.listingEmployer),
          label: offering ? '(Offer)' : '(Listing)',
          role: roles.listing,
        },
        contracted: {
          name: employerName(contract.contractedEmployer),
          label: offering ? '(Request)' : '(Response)',
          role: roles.contracted,
        },
      };
    }
    default:
      return {
        listing: {
          name: employerName(contract.listingEmployer),
          label: '(Listing)',
          role: roles.listing,
        },
        contracted: {
          name: providerName(contract.contractedProvider),
          label: '(Application)',
          role: roles.contracted,
        },
      };
  }
};

// [MCV-1727] Which signature the viewer records. `listingEmployerSignedAt` is the
// LISTING side's signature and `contractedPartySignedAt` the CONTRACTED side's,
// whatever the party type: for a SERVICE contract the listing side is the listing
// provider, despite the column name. This is the same side the backend
// `sign_contract` writes for the caller, so the Sign button, the signed check and
// the stored timestamp cannot disagree.
export type ContractSignSide = 'listing' | 'contracted';

export interface ContractViewer {
  currentEmployerId?: number | null;
  isProviderView?: boolean;
}

type ContractSignFields = Pick<
  ContractListItem,
  | 'listingType'
  | 'listingEmployerId'
  | 'contractedEmployerId'
  | 'listingEmployerSignedAt'
  | 'contractedPartySignedAt'
>;

// A provider is the listing party of a SERVICE contract (they listed the service)
// and the contracted party of a JOB contract (they were hired). An MSAAS contract
// is employer to employer, so a provider is never a party to it. Employers match
// on their own employer id: listing first, matching the backend.
//
// This assumes a single-role viewer: the page is either the provider view or the
// employer view. A Clerk user who has BOTH a Provider row and an EmployerUser row
// can be shown Sign for a side the server does not assign them (the server matches
// the caller against every employer membership and provider row, listing side
// first). That is tolerable because the server stays authoritative: it writes the
// side it resolves, and answers a wrong, repeated or out-of-state sign with
// 409 or 401, which the machine reports as an error toast.
//
// It also assumes a single employer membership: the client compares against one
// employerUser.employerId, while the server resolves ALL of the caller's employer
// memberships. For a contract reached only through a second employer membership,
// the card's Hiring/Hired pill and the Sign button can therefore be wrong. Again
// the server stays authoritative.
export const getViewerSignSide = (
  contract: ContractSignFields,
  { currentEmployerId, isProviderView = false }: ContractViewer
): ContractSignSide | null => {
  if (isProviderView) {
    if (contract.listingType === 'SERVICE') return 'listing';
    if (contract.listingType === 'JOB') return 'contracted';
    return null;
  }
  if (currentEmployerId == null) return null;
  if (contract.listingEmployerId === currentEmployerId) return 'listing';
  if (contract.contractedEmployerId === currentEmployerId) return 'contracted';
  return null;
};

export const hasViewerSigned = (contract: ContractSignFields, viewer: ContractViewer): boolean => {
  const side = getViewerSignSide(contract, viewer);
  if (side === 'listing') return contract.listingEmployerSignedAt != null;
  if (side === 'contracted') return contract.contractedPartySignedAt != null;
  return false;
};
